import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, OnInit, signal, untracked, viewChild } from '@angular/core';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';

import { SqlAggregateFunctionType } from '@models/helpers';
import {
  ActivityFieldType,
  AttachmentModel,
  BaseFieldValueType,
  TableFieldValueType,
} from '@models/module/fields/main';
import { TranslateModule } from '@ngx-translate/core';
import { CipoOutlineFieldComponent } from '@shared/components/fields/cipo-outline-field/cipo-outline-field.component';
import { CipoTextViewComponent } from '@shared/components/fields/cipo-text-view/cipo-text-view.component';
import { CipoTextViewConfig } from '@shared/components/fields/common';
import { ACCENT_BG_COLOR, WARN_BG_COLOR } from '@shared/consts';
import { UtilsService } from '@shared/services';
import { TableFieldConfig, TableFieldService } from '../common';

type DataSourceValueType = {
  value?: BaseFieldValueType;
  readonlyConfig: CipoTextViewConfig;
};

type DataSourceItemType = Record<string, DataSourceValueType>;

@Component({
  selector: 'cipo-table-field-view',
  templateUrl: './table-field-view.component.html',
  imports: [
    MatTableModule,
    CipoTextViewComponent,
    CipoOutlineFieldComponent,
    CommonModule,
    MatSortModule,
    TranslateModule,
  ],
})
export class TableFieldViewComponent implements OnInit {
  private readonly tableFieldService = inject(TableFieldService);
  private readonly utilsService = inject(UtilsService);
  matSort = viewChild(MatSort);
  readonly AGGREGATES = SqlAggregateFunctionType;

  tableField = input<ActivityFieldType<TableFieldValueType[]>>();
  entityInstanceId = input<number>();
  includeHistory = input(false);

  tableFields = signal<TableFieldConfig[]>(null);
  columnsDictionary = signal<Record<string, string>>({});

  dataSource = new MatTableDataSource<DataSourceItemType>([]);
  displayedColumns = computed(() => this.tableFields()?.map(field => field.key) ?? []);
  totalWidth = computed(() => this.tableFields()?.reduce((acc, field) => acc + (field.width || 1), 0) || 0);
  ds = computed(() => {
    const tableField = this.tableField();
    const rowsCount = Math.max(tableField.value.length, tableField.oldValue.length);
    for (let row = 0; row < rowsCount; row++) {}
  });
  hasFooter = computed(() => {
    const { aggregateOldValue, aggregateValue } = this.tableField();
    return Object.keys(aggregateOldValue ?? {}).length > 0 || Object.keys(aggregateValue ?? {}).length > 0;
  });

  constructor() {
    effect(() => {
      const tableField = this.tableField();
      const includeHistory = this.includeHistory();
      if (!tableField) {
        this.dataSource.data = [];
        return;
      }

      untracked(() => this.populateDataSource(tableField, includeHistory));
    });
  }

  ngOnInit(): void {
    this.dataSource.sort = this.matSort();
    this.dataSource.sortingDataAccessor = (item: DataSourceItemType, columnId: string): string | number => {
      const cell = item[columnId];
      if (!cell) return ''; // if column key is not present in this row
      const rawValue = cell.value ?? cell.readonlyConfig?.newValue ?? '';
      return typeof rawValue === 'string' || typeof rawValue === 'number' ? rawValue : String(rawValue);
    };
    const entityInstanceId = this.entityInstanceId();
    const { oldValue, value, id } = this.tableField();
    this.tableFieldService
      .getTableFields({ entityInstanceId, oldValue, value, id, viewMode: true })
      .subscribe(fields => {
        const tableFields: TableFieldConfig[] = [];
        const dict = {};
        /* We need to exclude the label, so we don't show it in the table on each row */
        fields.forEach(({ label, ...field }) => {
          dict[field.key] = label ?? field.key;
          tableFields.push(field);
        });
        this.tableFields.set(tableFields);
        this.columnsDictionary.set(dict);
      });
  }

  private populateDataSource(tableField: ActivityFieldType<TableFieldValueType[]>, includeHistory = false): void {
    if (!includeHistory) {
      this.dataSource.data =
        tableField.value?.map(value => {
          const dataItem: DataSourceItemType = {};
          for (const key in value) {
            if (value.hasOwnProperty(key)) {
              dataItem[key] = {
                value: value[key],
                readonlyConfig: {},
              };
            }
          }
          return dataItem;
        }) || [];
      return;
    }

    // This is data per row
    const valueArr = tableField.value ?? [];
    const oldValueArr = tableField.oldValue ?? [];

    const maxLength = Math.max(valueArr.length, oldValueArr.length);
    if (maxLength === 0) {
      this.dataSource.data = [];
      return;
    }

    const ds: DataSourceItemType[] = [];

    for (let i = 0; i < maxLength; i++) {
      const oldValEntry = oldValueArr[i] || {};
      const valEntry = valueArr[i] || {};

      const combinedArray: DataSourceItemType = {};
      for (const key of [...Object.keys(oldValEntry), ...Object.keys(valEntry)]) {
        const oldValue = oldValEntry[key];
        const newValue = valEntry[key];

        combinedArray[key] = this.utilsService.makeValueAndReadonlyConfig(oldValue, newValue);
      }

      ds.push(combinedArray);
    }
    this.dataSource.data = ds;
  }
}
